import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../App";
import { ArrowLeft, Users, ExternalLink, BookOpen, Lock, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const INFLUENCER_IMAGES = {
  lawrence_lepard: "https://customer-assets.emergentagent.com/job_371f6770-7243-467f-9ab5-7e075fb12251/artifacts/ulnc189q_lawrence%20lapard.webp",
  jack_mallers: "https://customer-assets.emergentagent.com/job_371f6770-7243-467f-9ab5-7e075fb12251/artifacts/0zlrebfg_jack%20mallers.webp",
  saifedean_ammous: "https://customer-assets.emergentagent.com/job_bbb94e1b-841c-4c06-983a-0eee528dac02/artifacts/tqgrl33n_saifa%20ammous.jpeg",
  michael_saylor: "https://customer-assets.emergentagent.com/job_bbb94e1b-841c-4c06-983a-0eee528dac02/artifacts/71rymv81_micheal%20saylor.jpeg",
  preston_pysh: "https://customer-assets.emergentagent.com/job_bbb94e1b-841c-4c06-983a-0eee528dac02/artifacts/nfjrch3o_preston%20pysh.jpeg",
  lyn_alden: "https://customer-assets.emergentagent.com/job_bbb94e1b-841c-4c06-983a-0eee528dac02/artifacts/b27dceb7_lynn%20alden.jpeg",
  jeff_booth: "https://customer-assets.emergentagent.com/job_371f6770-7243-467f-9ab5-7e075fb12251/artifacts/ddtl4egq_jeff%20booth.jpeg",
  robert_breedlove: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/jo8tbjqj_robert%20breedlove%202.jpeg",
  natalie_brunell: "https://customer-assets.emergentagent.com/job_bbb94e1b-841c-4c06-983a-0eee528dac02/artifacts/ctmecvf7_natalie%20brunnelle.jpeg",
  cathie_wood: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/qp2m8n7e_cathy%20woods.jpeg",
  jack_dorsey: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/4ly5y1uy_jack%20dorsey.jpeg",
  altcoin_daily: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/5byj4px1_alt%20coin%20daily.jpeg",
  hal_finney: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/lx84uf2x_hal%20finney.jpeg",
  satoshi_nakamoto: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/lxxrx8g8_satoshi%20nakamoto.jpeg",
  simply_bitcoin: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/zuukafow_simply%20bitcoin.jpeg",
  anthony_pompliano: "https://customer-assets.emergentagent.com/job_26de3264-ed82-4456-8844-6016d98e6030/artifacts/wz2zm00q_anthony%20pompliano.jpeg",
  toby_cunningham: "https://customer-assets.emergentagent.com/job_ff149fc4-84f0-43c7-b75f-bcfd56c9335d/artifacts/n8xxau4c_toby%20.jpeg"
};

export default function BrainTrust() {
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [easterEggTaps, setEasterEggTaps] = useState({});
  const EASTER_EGG_IDS = ["satoshi_nakamoto", "hal_finney"];

  const handleCardClick = (inf) => {
    if (EASTER_EGG_IDS.includes(inf.id)) {
      const taps = (easterEggTaps[inf.id] || 0) + 1;
      setEasterEggTaps(prev => ({ ...prev, [inf.id]: taps }));
      if (taps === 3) {
        const msg = inf.id === "satoshi_nakamoto"
          ? "You found Satoshi. +2,009 BTC XP — The Genesis Block lives in you."
          : "Running Bitcoin. +170 BTC XP — Hal's legacy endures forever.";
        toast.success(msg, { duration: 5000 });
        // Trigger easter egg API
        const eggId = inf.id === "satoshi_nakamoto" ? "satoshi_whitepaper" : "hal_finney_running";
        api.post(`/easter-eggs/claim-egg/${eggId}`).catch(() => {});
        setEasterEggTaps(prev => ({ ...prev, [inf.id]: 0 }));
      }
    }
    setSelected(selected?.id === inf.id ? null : inf);
  };
  const [loading, setLoading] = useState(true);
  const [unlockedSections, setUnlockedSections] = useState(new Set());

  useEffect(() => {
    Promise.all([
      api.get("/brain-trust/influencers"),
      api.get("/course/sections").catch(() => ({ data: { sections: [] } }))
    ]).then(([infRes, courseRes]) => {
      setInfluencers(infRes.data.influencers);
      const unlocked = new Set(
        courseRes.data.sections
          .filter(s => !s.locked)
          .map(s => s.id)
      );
      setUnlockedSections(unlocked);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="brain-trust-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">BITCOIN BRAIN <span className="text-[#F7931A]">TRUST</span></h1>
          <Users className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <p className="text-zinc-400 text-sm mb-4 text-center max-w-xl mx-auto">
          The voices that shaped Bitcoin Brain. Curated profiles of the thinkers, builders, and educators
          who help working families understand sound money.
        </p>
        <p className="text-zinc-600 text-xs mb-8 text-center max-w-lg mx-auto italic" data-testid="trust-disclaimer">
          These individuals are not affiliated with or endorsing Bitcoin Brain. They are featured because their work
          and ideas helped shape my understanding of Bitcoin and inspired this platform.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {influencers.map((inf, i) => (
            <motion.div
              key={inf.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`card-dark p-5 cursor-pointer transition-all ${
                EASTER_EGG_IDS.includes(inf.id)
                  ? "border-[#FFD700]/30 shadow-[0_0_15px_rgba(255,215,0,0.1)] hover:border-[#FFD700]/60 hover:shadow-[0_0_25px_rgba(255,215,0,0.2)]"
                  : "hover:border-[#F7931A]/50"
              }`}
              onClick={() => handleCardClick(inf)}
              data-testid={`influencer-${inf.id}`}
            >
              <div className="flex items-start gap-4">
                <img
                  src={INFLUENCER_IMAGES[inf.id]}
                  alt={inf.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#F7931A]/30"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-heading font-bold">{inf.name}</h3>
                  <p className="text-[#F7931A] text-xs">{inf.title}</p>
                </div>
              </div>

              <p className="text-zinc-400 text-xs mt-3 italic">"{inf.quote}"</p>

              {selected?.id === inf.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-4 pt-4 border-t border-white/10 space-y-3"
                >
                  <div>
                    <p className="text-zinc-500 text-xs uppercase mb-1">Core Thesis</p>
                    <p className="text-zinc-300 text-sm">{inf.thesis}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase mb-1">Appears in Sections</p>
                    <div className="flex flex-wrap gap-1">
                      {inf.course_sections.map(s => {
                        const isUnlocked = unlockedSections.has(s);
                        return (
                          <button
                            key={s}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isUnlocked) {
                                navigate(`/course/${s}`);
                              } else {
                                navigate("/pricing");
                              }
                            }}
                            className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${
                              isUnlocked
                                ? "bg-[#F7931A]/10 border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/20"
                                : "bg-zinc-800/50 border border-zinc-700/30 text-zinc-500 hover:bg-zinc-700/30"
                            }`}
                            data-testid={`section-link-${s}`}
                          >
                            {isUnlocked ? <BookOpen size={10} /> : <Lock size={10} />}
                            Section {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {inf.brief_sections?.length > 0 && (
                    <p className="text-zinc-500 text-xs">
                      Featured in Weekly Brief: {inf.brief_sections.join(", ")}
                    </p>
                  )}
                  {inf.content_links?.length > 0 && (
                    <a
                      href={inf.content_links[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-xs text-[#F7931A] hover:text-[#F7931A]/80 mt-1"
                      data-testid={`follow-link-${inf.id}`}
                    >
                      <ExternalLink size={12} />
                      Follow on X
                    </a>
                  )}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
